import { useGoals } from 'features/goals';
import { useMemo } from 'react';
import {
  TaskFilterOptions,
  useFilteredTasksQuery,
} from 'shared/hooks/queries/use-filtered-tasks-query';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useUser } from 'shared/hooks/use-user';
import { FilterDocument } from 'shared/types/filter-document';
import { Goal } from 'shared/types/goal';
import { ID, IDPrefixes } from 'shared/types/id';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';

const getGoalChildIdsRecursively = (goal: Goal, goals: Goal[]): ID[] => {
  const childGoals = goals.filter(
    (_goal) => _goal.parentIds?.[_goal.parentIds?.length - 1] === goal.id,
  );
  return childGoals.reduce<ID[]>(
    (acc, childGoal) => [
      ...acc,
      ...getGoalChildIdsRecursively(childGoal, goals),
    ],
    [],
  );
};

export type Options = { uncompletedOnly?: boolean };

export const useTasksByFilterDocument = (
  filterDocument: FilterDocument,
  { uncompletedOnly }: Options = {},
) => {
  const goals = useGoals();
  const user = useUser();
  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);

  const filters = useMemo<TaskFilterOptions>(() => {
    const lifeAreaIds = filterDocument.filters?.lifeArea?.include?.toSorted();
    // get all goals from the selected lifeArea's
    const lifeAreaGoalIds =
      lifeAreaIds
        ?.map((lifeAreaId) => {
          const _goals = goals.filter((goal) => goal.lifeAreaId === lifeAreaId);
          return _goals
            .map((goal) => [
              goal.id,
              ...getGoalChildIdsRecursively(goal, goals),
            ])
            .flat();
        })
        .flat()
        .sort() ?? [];
    const goalIds =
      filterDocument.filters?.goal?.include?.length || lifeAreaGoalIds.length
        ? Array.from(
            new Set([
              ...(filterDocument.filters?.goal?.include?.toSorted() ?? []),
              ...lifeAreaGoalIds,
            ]),
          )
        : undefined;

    // Get task IDs from selected tags (OR logic - union of all task IDs)
    const tagIds = filterDocument.filters?.tag?.include;
    const taskIdsFromTags = tagIds?.length
      ? Array.from(
          new Set(
            tagIds
              .map((tagId) =>
                relationshipsMap[createPrefixId(tagId, IDPrefixes.Tag)]?.reduce<
                  ID[]
                >((acc, prefixedId) => {
                  if (getPrefixedIdPrefix(prefixedId) === IDPrefixes.Task) {
                    acc.push(getPrefixedId(prefixedId));
                  }
                  return acc;
                }, []),
              )
              .flat(1),
          ),
        ).sort()
      : undefined;

    // Extract duration filter and convert minutes to milliseconds
    const durationFilter = filterDocument.filters?.duration;
    const minDuration = durationFilter?.more?.value
      ? durationFilter.more.value * 60 * 1000
      : undefined;
    const maxDuration = durationFilter?.less?.value
      ? durationFilter.less.value * 60 * 1000
      : undefined;

    return {
      lifeAreas: lifeAreaIds ?? undefined,
      goals: goalIds ?? undefined,
      types: filterDocument.filters?.type?.include?.toSorted() ?? undefined,
      priorities:
        filterDocument.filters?.priority?.include?.toSorted() ?? undefined,
      ids: taskIdsFromTags?.length ? taskIdsFromTags : undefined,
      minDuration,
      maxDuration,
    };
  }, [
    filterDocument.filters?.lifeArea?.include,
    filterDocument.filters?.goal?.include,
    filterDocument.filters?.tag?.include,
    filterDocument.filters?.duration,
    filterDocument.filters?.type?.include,
    filterDocument.filters?.priority?.include,
    goals,
    relationshipsMap,
  ]);

  const { data } = useFilteredTasksQuery(user?.uid, {
    ...filters,
    uncompletedOnly,
  });

  return useMemo(() => data ?? [], [data]);
};
